import React, { useState } from 'react';
import { CheckCheck, ChevronRight, ChevronDown } from 'lucide-react';
import { NotificationInfoType } from '../../../../schemas/notification.schemas';
import NotificationsRepo from '../../../../utils/repository/notificationsRepo';
import './notificationItem.styles.css';
import { Divider } from 'semantic-ui-react';

type NotificationItemProps = {
    notification: NotificationInfoType;
    onMarkedAsRead: (id: number) => void;
    onMarkedAsUnread: (id: number) => void;
};

const SHORT_MESSAGE_THRESHOLD = 120;

function formatRelativeTime(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) {
        return 'just now';
    }
    if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
    }
    if (diffHours < 24) {
        return `${diffHours}h ago`;
    }
    if (diffDays < 30) {
        return `${diffDays}d ago`;
    }
    return date.toLocaleDateString();
}

function NotificationItem({ notification, onMarkedAsRead, onMarkedAsUnread }: NotificationItemProps) {
    const [expanded, setExpanded] = useState(false);

    const handleMarkAsRead = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!notification.isRead) {
            NotificationsRepo.markAsRead(notification.id, () => {
                onMarkedAsRead(notification.id);
            });
        }
    };

    const handleMarkAsUnread = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (notification.isRead) {
            NotificationsRepo.markAsUnread(notification.id, () => {
                onMarkedAsUnread(notification.id);
            });
        }
    };

    const isExpandable = !!notification.message && notification.message.length > SHORT_MESSAGE_THRESHOLD;

    const handleToggleExpand = () => {
        if (isExpandable) {
            setExpanded(prev => !prev);
        }
    };

    const className = `notification-item ${notification.isRead ? 'notification-item--read' : 'notification-item--unread'}`;

    return (
        <div className={className} onClick={handleToggleExpand}>
            <div className="notification-item__header">
                <span className="notification-item__title">{notification.title}</span>
                <div className="notification-item__header-right">
                    {notification.isRead ? (
                        <button
                            className="notification-item__mark-unread-btn"
                            onClick={handleMarkAsUnread}
                            title="Mark as unread"
                        >
                            <CheckCheck size={18} />
                        </button>
                    ) : (
                        <button
                            className="notification-item__mark-read-btn"
                            onClick={handleMarkAsRead}
                            title="Mark as read"
                        >
                            <CheckCheck size={18} />
                        </button>
                    )}
                    <span className="notification-item__time">{formatRelativeTime(notification.createdAt)}</span>
                </div>
            </div>
            <Divider />
            {notification.message && (
                <div className="notification-item__body">
                    {isExpandable ? (
                        <>
                            <span className="notification-item__chevron">
                                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                            {expanded ? (
                                <p className="notification-item__message">{notification.message}</p>
                            ) : (
                                <p className="notification-item__preview">{notification.message.slice(0, SHORT_MESSAGE_THRESHOLD)}...</p>
                            )}
                        </>
                    ) : (
                        <p className="notification-item__message-inline">{notification.message}</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default NotificationItem;
